import type { DocumentLink } from "@/models/document-link/DocumentLink";
import { RefType } from "@/models/document-link/DocumentLink";
import {
  getOtherSideLegislationTitleForTooltip,
  getOtherSideLegislationTypeId,
  getOtherSideReferenceLabel,
  isOtherSideHistorical,
  isOtherSideWorkspaceLink,
  isOutgoingReferenceFromCurrentDoc,
  parseLinkExtraMetadata,
} from "@/utils/documentLinkOtherSide";

/**
 * Retsinformation document types that count as guidance ("vejledning") for
 * {@link selectGuidanceLinks}: the plain Vejledning and Skatteministeriet's
 * "store vejledninger". Cirkulærer and skrivelser are deliberately out — they
 * share a Retsinformation reference group with vejledninger, but calling them
 * "Vejledning" in the toolbar would misname them.
 */
const GUIDANCE_DOCUMENT_TYPE_IDS = new Set(["180", "1490"]);

export function isGuidanceDocumentTypeId(typeId?: string | null): boolean {
  const trimmed = typeId?.trim();
  return trimmed ? GUIDANCE_DOCUMENT_TYPE_IDS.has(trimmed) : false;
}

/** One guidance document related to the open legislation document. */
export type GuidanceLink = {
  /** The reference this entry navigates through. */
  link: DocumentLink;
  /** External id of the guidance document itself (the other end of the link). */
  documentId: string;
  /** Short name where Retsinformation gave us one, e.g. "VEJ nr 9175 af 03/03/2021". */
  label: string;
  /** Official or popular title, for the tooltip / secondary line. */
  title?: string;
  /** Superseded or revoked guidance, shown last and marked. */
  isHistorical: boolean;
};

/**
 * The guidance documents reachable from the open legislation document, in the
 * order the toolbar lists them: current guidance first, then historical, each
 * alphabetised by label.
 *
 * Both directions of the relation count. Retsinformation records it on the
 * executive order as "Alle cirkulærer, vejledninger m.v. til denne
 * bekendtgørelse", and on the guidance itself as "Lovgivning forskriften
 * vedrører" — a given pair may only have one of the two, and the same guidance
 * can arrive on both.
 */
export function selectGuidanceLinks(
  links: DocumentLink[] | undefined,
  documentId: string | undefined
): GuidanceLink[] {
  if (!links?.length || !documentId) return [];

  const byDocumentId = new Map<string, GuidanceLink>();

  for (const link of links) {
    const touchesOrigin = link.originDocumentId === documentId;
    const touchesDestination = link.destinationDocumentId === documentId;
    if (!touchesOrigin && !touchesDestination) continue;

    // Only the document-level relation counts. A vejledning merely cited in the
    // body arrives as a text-level link, and presenting it as "the guidance for
    // this rule" would be wrong — Retsinformation puts the real relation in a
    // named reference group, so the header has to be there too.
    const metadata = parseLinkExtraMetadata(link.extraMetadata);
    if (metadata.ref_type !== RefType.DOCUMENT_TO_DOCUMENT) continue;
    if (!metadata.header?.trim()) continue;

    if (isOtherSideWorkspaceLink(link, documentId)) continue;

    const isOutgoing = isOutgoingReferenceFromCurrentDoc(link, documentId);
    if (
      !isGuidanceDocumentTypeId(getOtherSideLegislationTypeId(link, isOutgoing))
    ) {
      continue;
    }

    const otherSideId = isOutgoing
      ? link.destinationDocumentId
      : link.originDocumentId;
    if (!otherSideId) continue;

    const entry: GuidanceLink = {
      link,
      documentId: otherSideId,
      label: getOtherSideReferenceLabel(link, documentId),
      title: getOtherSideLegislationTitleForTooltip(link, documentId),
      isHistorical: isOtherSideHistorical(link, documentId),
    };

    // The same guidance document can arrive from both directions at once, so
    // collapse by its id — preferring the row that does not call it historical.
    // Successive accessions of one guidance have distinct ids and stay separate
    // entries; the sort below puts the current one first.
    const existing = byDocumentId.get(otherSideId);
    if (!existing || (existing.isHistorical && !entry.isHistorical)) {
      byDocumentId.set(otherSideId, entry);
    }
  }

  return [...byDocumentId.values()].sort((a, b) => {
    if (a.isHistorical !== b.isHistorical) return a.isHistorical ? 1 : -1;
    return a.label.localeCompare(b.label, "da", { sensitivity: "base" });
  });
}
